// ProjectFormDrawer — the projects side panel in BOTH of its modes, since the
// create and edit forms are the same fields over two verbs (PATCH takes the
// create payload with every key optional). Fields per the design: name*,
// description, inline icon grid, library tags (select-only TagsPicker — tag
// creation lives elsewhere), priority pills, start/end dates (sent as ISO
// datetimes) and the Members people+groups picker. Actions live in the footer
// (drawer convention).
//
// CREATE (no `projectId`) posts /projects/create with the whole draft.
// EDIT (`projectId`) seeds from GET /projects/detail/:id — the listing row
// can't seed it, carrying neither description nor member ids — and PATCHes
// /projects/update/:id with ONLY the changed keys, so it can never clobber a
// field the user didn't touch.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Button,
  Chip,
  Drawer,
  Field,
  Icon,
  Input,
  MembersPicker,
  TagsPicker,
  Textarea,
} from '@/components/common';
import {
  PROJECT_ICONS,
  useCreateProjectMutation,
  useGetProjectDetailQuery,
  useUpdateProjectMutation,
  type ProjectDetail,
  type ProjectPriority,
  type UpdateProjectPayload,
} from '@/api/endpoints/projectsApi';
import { useGetProjectTagsQuery } from '@/api/endpoints/tagsApi';
import { useAppSelector } from '@/store/hooks';
import { selectCompanyUserId } from '@/redux/auth/selectors';
import { toDateInput, toIso } from '@/utils/date';

interface ProjectFormDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Present = edit mode: the drawer seeds from the project's detail and saves
   *  a changed-keys-only PATCH. Absent = create mode. */
  projectId?: string;
}

// All four backend priorities, severity descending, in both modes — matching
// the Settings tab's priority Select and the listing filter. Urgent takes the
// table chip's `signal` icon, since Chip has no tone above danger and the two
// share it.
const PRIORITIES: { value: ProjectPriority; icon: string }[] = [
  { value: 'urgent', icon: 'signal' },
  { value: 'high', icon: 'chevrons-up' },
  { value: 'medium', icon: 'equal' },
  { value: 'low', icon: 'chevron-down' },
];

const emptyDraft = {
  title: '',
  description: '',
  icon: 'folder-kanban',
  priority: 'medium' as ProjectPriority,
  tagIds: [] as string[],
  memberIds: [] as string[],
  groupIds: [] as string[],
  startDate: '',
  dueDate: '',
};

type ProjectDraft = typeof emptyDraft;

/** The editable slice of a project — the edit draft, and the baseline it diffs
 *  against. Mirrors `emptyDraft` key for key so one form serves both modes. */
const seedFromDetail = (project: ProjectDetail): ProjectDraft => ({
  title: project.name,
  description: project.description,
  icon: project.icon,
  priority: project.priority,
  tagIds: project.tags.map((tag) => tag.id),
  memberIds: project.members.map((m) => m.id),
  groupIds: project.groups.map((g) => g.id),
  startDate: toDateInput(project.startDate),
  dueDate: toDateInput(project.endDate),
});

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

const ProjectFormDrawer = ({ open, onClose, projectId }: ProjectFormDrawerProps) => {
  const { t } = useTranslation();
  const editing = !!projectId;

  const { data: tags = [] } = useGetProjectTagsQuery(undefined, { skip: !open });
  const { data: detail } = useGetProjectDetailQuery(projectId as string, {
    skip: !open || !projectId,
  });
  const [createProject, { isLoading: creating }] = useCreateProjectMutation();
  const [updateProject, { isLoading: updating }] = useUpdateProjectMutation();
  const saving = creating || updating;

  // The creator is a member from the start: seeded into the draft (so it ships
  // in the create body) and locked in the picker. Narrowed with `as` — the
  // drawer only renders inside the authenticated app, where select-company has
  // always stored it (and the company token refresh re-stores the full
  // session, so even sessions persisted before the field existed backfill it).
  const companyUserId = useAppSelector(selectCompanyUserId) as string;

  // The baseline: an empty draft carrying the creator when creating, the
  // project's own values when editing (null until its detail lands).
  const seed = useMemo<ProjectDraft | null>(() => {
    if (!editing) return { ...emptyDraft, memberIds: [companyUserId] };
    return detail ? seedFromDetail(detail) : null;
  }, [editing, detail, companyUserId]);

  const [draft, setDraft] = useState<ProjectDraft>(emptyDraft);
  const [submitted, setSubmitted] = useState(false);

  // Re-seed each time the drawer opens — and again when an edit's detail lands
  // (or comes back changed after a save refetch); leftovers die on close.
  useEffect(() => {
    if (open && seed) {
      setDraft(seed);
      setSubmitted(false);
    }
  }, [open, seed]);

  const patch = (changes: Partial<ProjectDraft>) => setDraft((d) => ({ ...d, ...changes }));

  const tagById = useMemo(() => new Map(tags.map((tag) => [tag.id, tag.title])), [tags]);

  const removeTag = (id: string) => patch({ tagIds: draft.tagIds.filter((x) => x !== id) });

  // "N people · N groups" beside the Members label (the trigger shows it too).
  const memberSummary = useMemo(() => {
    const parts: string[] = [];
    if (draft.memberIds.length > 0)
      parts.push(
        draft.memberIds.length === 1
          ? t('members_person')
          : t('members_people', { count: draft.memberIds.length }),
      );
    if (draft.groupIds.length > 0)
      parts.push(t('members_groups_suffix', { count: draft.groupIds.length }));
    return parts.join(' · ');
  }, [draft.memberIds, draft.groupIds, t]);

  const datesInvalid = !!draft.startDate && !!draft.dueDate && draft.dueDate < draft.startDate;
  const titleMissing = !draft.title.trim();
  const startMissing = !draft.startDate;
  const endMissing = !draft.dueDate;

  // Save is dead until something actually changed, so editing can never fire an
  // empty PATCH. Creating is validated instead — its baseline is always dirty.
  const dirty =
    !!seed &&
    (draft.title !== seed.title ||
      draft.description !== seed.description ||
      draft.icon !== seed.icon ||
      draft.priority !== seed.priority ||
      draft.startDate !== seed.startDate ||
      draft.dueDate !== seed.dueDate ||
      !sameIds(draft.tagIds, seed.tagIds) ||
      !sameIds(draft.memberIds, seed.memberIds) ||
      !sameIds(draft.groupIds, seed.groupIds));

  const submit = async () => {
    // Required-field validation runs BEFORE any backend call: mark the form as
    // submitted so errors show, and bail while anything is invalid.
    setSubmitted(true);
    if (titleMissing || startMissing || endMissing || datesInvalid) return;
    const name = draft.title.trim();
    try {
      if (editing && seed) {
        // Diff against the seed — untouched fields stay out of the body
        // entirely, so a field this drawer didn't change can't be clobbered.
        const body: UpdateProjectPayload = {};
        if (draft.title !== seed.title) body.title = name;
        if (draft.description !== seed.description) body.description = draft.description.trim();
        if (draft.icon !== seed.icon) body.icon = draft.icon;
        if (draft.priority !== seed.priority) body.priority = draft.priority;
        if (draft.startDate !== seed.startDate) body.startDate = toIso(draft.startDate);
        if (draft.dueDate !== seed.dueDate) body.dueDate = toIso(draft.dueDate);
        if (!sameIds(draft.tagIds, seed.tagIds)) body.tags = draft.tagIds;
        if (!sameIds(draft.memberIds, seed.memberIds)) body.members = draft.memberIds;
        if (!sameIds(draft.groupIds, seed.groupIds)) body.groups = draft.groupIds;
        await updateProject({ id: projectId as string, body }).unwrap();
        toast.success(t('projects_updated', { name }));
      } else {
        await createProject({
          title: name,
          description: draft.description.trim(),
          members: draft.memberIds,
          groups: draft.groupIds,
          priority: draft.priority,
          icon: draft.icon,
          startDate: toIso(draft.startDate),
          dueDate: toIso(draft.dueDate),
          tags: draft.tagIds,
        }).unwrap();
        toast.success(t('projects_created', { name }));
      }
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Editing waits on the detail call; creating never does.
  const loading = editing && !seed;
  // The owner can never be removed from their own project; when creating, the
  // creator holds that place. `lockedUserIds` is presentational only.
  const lockedUserIds = editing ? (detail ? [detail.owner.id] : []) : [companyUserId];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      icon={editing ? 'pencil' : 'folder-plus'}
      title={t(editing ? 'projects_edit_title' : 'projects_create_title')}
      subtitle={t(editing ? 'projects_edit_subtitle' : 'projects_create_subtitle')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={saving}>
            {t('button_cancel')}
          </Button>
          <Button
            iconStart={editing ? 'check' : 'plus'}
            onClick={submit}
            loading={saving}
            disabled={loading || (editing && !dirty)}
          >
            {t(editing ? 'projects_settings_save' : 'projects_create')}
          </Button>
        </>
      }
    >
      {loading ? (
        <div className='project-form__loading'>
          <Icon name='loader' size={26} />
          {t('text_loading')}
        </div>
      ) : (
        <div className='project-form'>
          <Field
            label={t('projects_field_name')}
            error={submitted && titleMissing ? t('projects_name_required') : undefined}
          >
            <Input
              value={draft.title}
              placeholder={t('projects_name_placeholder')}
              error={submitted && titleMissing}
              onChange={(e) => patch({ title: e.target.value })}
            />
          </Field>

          <Field label={t('projects_field_desc')}>
            <Textarea
              rows={2}
              placeholder={t('projects_desc_placeholder')}
              value={draft.description}
              onChange={(e) => patch({ description: e.target.value })}
            />
          </Field>

          <Field label={t('projects_field_icon')}>
            <div className='project-form__icons'>
              <span className='project-form__icon-preview'>
                <Icon name={draft.icon} size={22} />
              </span>
              <div className='project-form__icon-grid'>
                {PROJECT_ICONS.map((icon) => (
                  <button
                    key={icon}
                    type='button'
                    className={`project-form__icon${draft.icon === icon ? ' is-selected' : ''}`}
                    aria-label={icon}
                    aria-pressed={draft.icon === icon}
                    onClick={() => patch({ icon })}
                  >
                    <Icon name={icon} size={17} />
                  </button>
                ))}
              </div>
            </div>
          </Field>

          <Field label={t('projects_field_tags')}>
            <div className='project-form__tags'>
              {draft.tagIds.length > 0 && (
                <div className='project-form__tag-chips'>
                  {draft.tagIds.map((id) => (
                    <Chip key={id} variant='brand' iconStart='tag'>
                      {tagById.get(id) ?? id}
                      <button
                        type='button'
                        className='project-form__tag-remove'
                        aria-label={t('unit_tag_remove', { tag: tagById.get(id) ?? id })}
                        onClick={() => removeTag(id)}
                      >
                        <Icon name='x' size={12} />
                      </button>
                    </Chip>
                  ))}
                </div>
              )}

              {tags.length === 0 ? (
                <span className='project-form__tags-empty'>{t('projects_tags_none')}</span>
              ) : (
                <TagsPicker
                  tags={tags}
                  selectedIds={draft.tagIds}
                  onChange={(ids) => patch({ tagIds: ids })}
                  placeholder={t('projects_tags_pick')}
                />
              )}
            </div>
          </Field>

          <Field label={t('projects_col_priority')}>
            <div
              className='project-form__priority'
              role='group'
              aria-label={t('projects_col_priority')}
            >
              {PRIORITIES.map((p) => {
                const selected = draft.priority === p.value;
                return (
                  <button
                    key={p.value}
                    type='button'
                    aria-pressed={selected}
                    className={`project-form__priority-btn project-form__priority-btn--${p.value}${
                      selected ? ' is-selected' : ''
                    }`}
                    onClick={() => patch({ priority: p.value })}
                  >
                    <Icon name={p.icon} size={15} />
                    {t(`projects_priority_${p.value}`)}
                  </button>
                );
              })}
            </div>
          </Field>

          <div className='project-form__dates'>
            <Field
              label={t('projects_field_start')}
              labelTag='· DD-MM-YYYY'
              error={submitted && startMissing ? t('projects_start_required') : undefined}
            >
              <Input
                type='date'
                iconStart='calendar'
                error={submitted && startMissing}
                value={draft.startDate}
                onChange={(e) => patch({ startDate: e.target.value })}
              />
            </Field>
            <Field
              label={t('projects_field_end')}
              labelTag='· DD-MM-YYYY'
              error={
                submitted && endMissing
                  ? t('projects_end_required')
                  : datesInvalid
                    ? t('projects_dates_invalid')
                    : undefined
              }
            >
              <Input
                type='date'
                iconStart='calendar-check'
                error={(submitted && endMissing) || datesInvalid}
                value={draft.dueDate}
                onChange={(e) => patch({ dueDate: e.target.value })}
              />
            </Field>
          </div>

          <Field
            className='project-form__members'
            label={t('projects_field_members')}
            labelTag={
              <>
                · {t('projects_members_hint')}
                {memberSummary && (
                  <span className='project-form__members-count'>{memberSummary}</span>
                )}
              </>
            }
          >
            <MembersPicker
              selectedUserIds={draft.memberIds}
              selectedGroupIds={draft.groupIds}
              lockedUserIds={lockedUserIds}
              onChangeUsers={(ids) => patch({ memberIds: ids })}
              onChangeGroups={(ids) => patch({ groupIds: ids })}
            />
          </Field>
        </div>
      )}
    </Drawer>
  );
};

export default ProjectFormDrawer;
